import React, { useState, useContext } from "react";
import Card from "./Card";
import { YearContext, AuthContext } from "../../context/context";
import { DateTime } from "luxon";
import { TbFocus2, TbCirclePlus, TbEye } from "react-icons/tb";
import { submitNote, deleteNote, deleteGoal } from "../../api";
import NavSidebar from "../Sidebar/NavSidebar";
import NavSidebarEdit from "../Sidebar/NavSidebarEdit";
import CardAddNote from "./CardAddNote";
import CardWishlist from "./CardWishlist";
import { useLanguage } from "../../context/LanguageContext";
import CardMilestones from "./CardMilestones";
import dayjs from "dayjs";
import CardProgress from "./CardProgress";
import GoalCardOneRow from "./CardGoalOneRow";
import CardProgressJournal from "./CardProgressJournal";
import CardProgressGoal from "./CardProgressGoal";
import BtnSecondaryS from "../Buttons/BtnSecondaryS";

// Simple hook to detect mobile (md breakpoint)
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 768);
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

const TabSection = () => {
  const today = DateTime.local();
  const todayNote = today.toISODate();
  const { selectedYear } = useContext(YearContext);
  const { userData, setUserData } = useContext(AuthContext);
  const { copy } = useLanguage();

  const [activeTab, setActiveTab] = useState(1);
  const [sidebarToggled, setSidebarToggled] = useState(false);
  const [sidebarEditToggled, setSidebarEditToggled] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);

  const isMobile = useIsMobile();
  const tabLabels = [
    copy.tab.title_1,
    copy.tab.title_2,
    copy.tab.title_3,
    copy.tab.title_4,
  ];

  const filteredNotes =
    userData?.notes?.filter((note) => {
      const noteStr = dayjs(note.note_date).format("YYYY-MM-DD");
      return noteStr === todayNote;
    }) || [];

  const handleAddNote = async (title, description, note_date) => {
    try {
      await submitNote(
        title,
        description,
        note_date,
        userData.id,
        selectedYear
      );
    } catch (err) {
      console.error("Failed to submit note:", err.message);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await deleteNote(noteId);
    } catch (err) {
      console.error("Error deleting note:", err.message);
    }
  };

  const goals = Array.isArray(userData?.goals) ? userData.goals : [];
  const goalData = selectedYear
    ? goals.filter((item) => item.year === selectedYear)
    : goals;

  const goalDropdown = [
    {
      text: copy.dropdown.add,
      onClick: () => setSidebarToggled(true),
      icon: <TbCirclePlus />,
    },
    { text: copy.dropdown.view_all, link: "/goals", icon: <TbEye /> },
  ];

  const handleEditClick = (goal) => {
    setSelectedGoal(goal);
    setSidebarEditToggled(true);
  };

  const handleDeleteClick = async (goalId) => {
    try {
      console.log("Deleting goal with id:", goalId);
      const result = await deleteGoal(goalId);

      if (!result) {
        console.error("Failed to delete goal");
        return;
      }

      console.log("Goal deleted successfully");

      const updatedGoals = goals.filter((goal) => goal.id !== goalId);
      const newUserData = { ...userData, goals: updatedGoals };
      setUserData(newUserData);
    } catch (err) {
      console.error("Error deleting goal:", err.message);
    }
  };

  return (
    <div className="flex flex-col gap-1 h-full">
      {/* Tabs */}
      <div className="flex flex-row gap-2 mb-4 card-base-border card-base card-base-background p-1.5 overflow-x-auto overflow-y-hidden hide-scrollbar whitespace-nowrap">
        {tabLabels.map((label, idx) => {
          // Skip the first tab (Goals & progress) - COMMENTED OUT FOR NOW
          if (idx === 0) return null;
          
          return (
            <div
              key={label}
              className={`py-2 px-4 cursor-pointer card-base rounded-md card-base-border text-lg md:text-base ${activeTab === idx ? "bg-accent-light " : " hover:shadow-sm "} `}
              onClick={() => setActiveTab(idx)}
            >
              {label}
            </div>
          );
        })}
      </div>

      {/* Tabs content */}
      <div className="w-full h-[380px]">
        {/* Milestones Tab - COMMENTED OUT FOR NOW */}
        {/* {activeTab === 0 && (
          <div className="grid grid-cols-1 md:grid-cols-5 w-full gap-2 h-full">
            <div className="col-span-1 md:col-span-3 order-2 md:order-1">
              {/* Goals Card */}
              {/* <Card
                iconTop={TbFocus2}
                title={`${copy.card_goal.headline} ${selectedYear}`}
                description={copy.card_goal.description}
                options={goalDropdown}
                useMaxHeight={true}
                onClick={() => setSidebarToggled(true)}
              >
                {goalData.length === 0 ? (
                  <p>No goals found for this year.</p>
                ) : (
                  <div className="grid grid-cols-1 gap-4 overflow-auto max-h-full hide-scrollbar">
                    {Object.entries(
                      goalData.reduce((acc, goal) => {
                        const rawCategory = goal.category || "Uncategorized";
                        const key = rawCategory.toLowerCase();
                        if (!acc[key])
                          acc[key] = { display: rawCategory, goals: [] };
                        acc[key].goals.push(goal);
                        if (acc[key].goals.length === 1) {
                          acc[key].display =
                            rawCategory.charAt(0).toUpperCase() +
                            rawCategory.slice(1).toLowerCase();
                        }
                        return acc;
                      }, {})
                    ).map(([key, { display, goals }]) => (
                      <div key={key} className="flex flex-col gap-1">
                        <h3 className="font-semibold  md:text-base text-lg">
                          {display}
                        </h3>
                        {goals.map((goal) => {
                          const goalMilestones = (userData.milestones || []).filter(
                            (milestone) => milestone.goal_id === goal.id
                          );
                          return (
                            <div key={goal.id}>
                              <GoalCardOneRow
                                title={goal.title}
                                goal={goal}
                                goalMilestones={goalMilestones}
                                onEdit={handleEditClick}
                                onDelete={handleDeleteClick}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ))}
                    <div className="mt-4 pb-4 flex justify-center">
                      <BtnSecondaryS
                        buttonCTA="View all"
                        onClickLink={() => window.location.href = '/goals'}
                        type="button"
                      />
                    </div>
                  </div>
                )}
              </Card>
            </div>

            <div className="col-span-1 md:col-span-2 order-1 md:order-2 mt-2 md:mt-0">
              {/* Progress Card */}
              {/* <CardProgressGoal />
            </div>
          </div>
        )} */}

        {/* Goals Tab */}
        {activeTab === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-5 w-full gap-2 h-full items-stretch">
            <div className="col-span-1 md:col-span-3 order-2 md:order-1 flex min-h-0">
              <CardMilestones
                className="md:pr-1 md:overflow-auto hide-scrollbar"
                activeDay={today}
                showViewInCalendarButton={true}
                isInTab={true}
                cardClassName="h-full"
              />
            </div>
            <div className="col-span-1 md:col-span-2 order-1 md:order-2 mt-2 md:mt-0 flex min-h-0">
              <CardProgress cardClassName="h-full" />
            </div>
          </div>
        )}

        {/* Daily Journal Tab */}
        {activeTab === 2 && (
          <div className="grid grid-cols-1 md:grid-cols-5 w-full gap-2 h-full items-stretch">
            <div className="md:col-span-2 col-span-1 flex min-h-0">
              <CardAddNote
                notes={filteredNotes}
                activeDay={today}
                onSubmitNote={handleAddNote}
                onDeleteNote={handleDeleteNote}
                cardClassName="h-full"
              />
            </div>
            <div className="md:col-span-3 col-span-1 flex min-h-0">
              <CardProgressJournal cardClassName="h-full" />
            </div>
          </div>
        )}

        {/* Wishlist Tab */}
        {activeTab === 3 && (
          <div className="flex w-full gap-2 h-full items-stretch min-h-0">
            <CardWishlist
              showTitle={isMobile}
              showPrice={isMobile}
              className="md:pr-1 md:overflow-auto"
              showViewAllLink={true}
              cardClassName="h-full"
            />
          </div>
        )}
      </div>

      {/* Sidebars */}
      {sidebarToggled && (
        <NavSidebar setSidebarToggled={() => setSidebarToggled(false)} />
      )}
      {sidebarEditToggled && (
        <NavSidebarEdit
          setSidebarEditToggled={setSidebarEditToggled}
          selectedGoal={selectedGoal}
        />
      )}
    </div>
  );
};

export default TabSection;
